import { Stepper, type StepperStep } from '@workspace/ui/components/stepper'
import { ArrowUpRight, Check, ShieldCheck } from 'lucide-react'
import { OUTREACH_HELP_URL, OUTREACH_SAFETY_LINE } from '../config/launch-checklist'
import { useLaunchFlow } from '../context/launch-flow-context'
import type { LaunchStep } from '../hooks/use-outreach-launch-checklist'
import { DisconnectLinkedInButton } from './disconnect-linkedin-button'
import { OutreachSetupCta } from './outreach-setup-cta'

// The setup surface: a step progress bar (pending steps clickable — jumping
// ahead lands on the first incomplete step; done steps are inert) plus a
// single primary CTA for the next incomplete step. When all four are done the
// CTA launches the campaign directly. Pre-launch only: the post-launch waiting
// modes this used to render restated the hero and carried no action, so the
// hero owns that phase now.
export function OutreachLaunchPanel() {
  const { startupId, checklist, openStep, editMessages, launch } = useLaunchFlow()
  const stepDone = (id: LaunchStep['id']) =>
    checklist.steps.some((step) => step.id === id && step.done)
  const reviewDone = stepDone('review')
  const linkedinDone = stepDone('linkedin')

  // The stepper shows the four setup steps plus a final "Launch campaign" node
  // (display-only record of the goal). It's never "done" — it's the terminal
  // action — so it lights up as the current step once the four are complete, and
  // clicking it launches (the primary CTA below does the same).
  const stepperSteps: StepperStep[] = [
    ...checklist.steps,
    { id: 'launch', label: 'Launch campaign', done: false },
  ]
  const currentStepId: string | null =
    checklist.firstIncomplete?.id ?? (checklist.allComplete ? 'launch' : null)

  return (
    <section className="mb-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-card/60 to-card/20 px-6 py-6">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
        Finish setup to launch
      </div>
      <h2 className="mt-1 text-[20px] font-bold tracking-[-0.01em] text-foreground">
        {checklist.allComplete
          ? 'Everything is ready.'
          : checklist.completedCount === 0
            ? `${checklist.steps.length} steps to launch`
            : `${checklist.completedCount} of ${checklist.steps.length} steps done`}
      </h2>
      {!checklist.allComplete && (
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          Includes a one-time LinkedIn sign-in.
        </p>
      )}

      <Stepper
        steps={stepperSteps}
        currentId={currentStepId}
        onStepClick={(id) => (id === 'launch' ? launch() : openStep(id as LaunchStep['id']))}
      />
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <OutreachSetupCta />
      </div>

      <div className="mt-4 space-y-1.5">
        <div className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground">
          <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-emerald-400" />
          <p>{OUTREACH_SAFETY_LINE}</p>
        </div>
        <a
          href={OUTREACH_HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 pl-[22px] text-[11.5px] font-medium text-primary hover:text-primary/80"
        >
          How the LinkedIn connection works
          <ArrowUpRight className="size-3" />
        </a>
        {(reviewDone || linkedinDone) && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 pl-[22px] pt-1">
            {reviewDone && (
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-400">
                <Check className="size-3.5" strokeWidth={2.5} />
                Intros approved
                <span className="text-muted-foreground/60">·</span>
                <button
                  type="button"
                  onClick={() => editMessages()}
                  className="font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                >
                  Edit messages
                </button>
              </div>
            )}
            {linkedinDone && (
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-400">
                <Check className="size-3.5" strokeWidth={2.5} />
                LinkedIn connected
                <span className="text-muted-foreground/60">·</span>
                <DisconnectLinkedInButton startupId={startupId} variant="inline" />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
